import React, { useContext, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import { PieChart as PieChartIcon, X } from "lucide-react";
import { ClientsContext, ClientGroupsContext } from "@/App";
import { fmtMoney } from "./utils";

/**
 * ShippedByClientDonut — lightbox showing this month's shipped lines as a
 * donut, sliced by client (or client group), with line counts and value
 * per slice. Requested by the chairman: "which clients' stuff is getting
 * shipped out this month and of how much value each."
 *
 * - Slices are sized by VALUE when the viewer can see values (the backend
 *   strips value_usd for users without can_view_value — in that case we
 *   detect the absence and fall back to count-sized slices, no $ shown).
 * - Slice colours are each client's own RockLink colour; groups borrow the
 *   colour of their first member, falling back to a fixed palette.
 * - Beyond 6 slices the tail rolls up into a neutral "Others".
 */

const OTHERS_COLOR = "#94a3b8";
const GROUP_FALLBACK_COLORS = ["#3b82f6", "#a855f7", "#14b8a6", "#f59e0b", "#ec4899", "#84cc16"];

function isShippedThisMonth(item, now) {
  if (!item.shipped_at) return false;
  const shipped = item.stage === "Completed"
    || (item.stage === "Dispatch" && (item.dispatch_status === "Shipped" || item.dispatch_status === "Delivered"));
  if (!shipped) return false;
  const d = new Date(item.shipped_at);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function DonutTooltip({ active, payload, canSeeValue }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--popover))] px-2.5 py-1.5 text-[11px] shadow-md">
      <div className="font-semibold flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
        {p.name}
      </div>
      <div className="font-mono-data text-[hsl(var(--muted-foreground))] mt-0.5">
        {p.isScrap
          ? `${fmtMoney(p.value)} · by-product`
          : `${p.lines} line${p.lines === 1 ? "" : "s"}${canSeeValue ? ` · ${fmtMoney(p.value)}` : ""}`}
      </div>
    </div>
  );
}

const SCRAP_COLOR = "#7c3aed"; // Sales-area violet — scrap is a distinct by-product slice

export default function ShippedByClientDonut({ open, onClose, items, monthLabel, scrapThisMonth = 0, canViewScrap = false }) {
  const { clients } = useContext(ClientsContext);
  const { clientGroups } = useContext(ClientGroupsContext);
  const [mode, setMode] = useState("clients"); // "clients" | "groups"

  const shipped = useMemo(() => {
    const now = new Date();
    return (items || []).filter(i => isShippedThisMonth(i, now));
  }, [items]);

  // Scrap sales for this month — a by-product revenue slice, value only (no line
  // count). Present only when the viewer holds scrap-view permission.
  const hasScrap = canViewScrap && scrapThisMonth > 0;
  // Scrap is a pure value figure, so its presence implies the viewer can see money.
  const canSeeValue = useMemo(() => hasScrap || shipped.some(i => typeof i.value_usd === "number"), [shipped, hasScrap]);

  const colorByClient = useMemo(
    () => Object.fromEntries((clients || []).map(c => [c.name, c.color])),
    [clients]
  );
  const groupByClientName = useMemo(() => {
    const idToGroup = {};
    (clientGroups || []).forEach(g => (g.client_ids || []).forEach(id => { idToGroup[id] = g.name; }));
    const m = {};
    (clients || []).forEach(c => { m[c.name] = idToGroup[c.id] || null; });
    return m;
  }, [clients, clientGroups]);

  const { slices, totalLines, totalValue } = useMemo(() => {
    const agg = new Map();
    for (const it of shipped) {
      const client = (it.client || "—").trim() || "—";
      const key = mode === "groups" ? (groupByClientName[client] || client) : client;
      const e = agg.get(key) || { name: key, lines: 0, value: 0, color: null };
      e.lines += 1;
      e.value += Number(it.value_usd) || 0;
      if (!e.color && mode === "clients") e.color = colorByClient[client] || null;
      agg.set(key, e);
    }
    const metric = canSeeValue ? (e) => e.value : (e) => e.lines;
    let arr = [...agg.values()].sort((a, b) => metric(b) - metric(a));
    arr.forEach((e, i) => { if (!e.color) e.color = GROUP_FALLBACK_COLORS[i % GROUP_FALLBACK_COLORS.length]; });
    if (arr.length > 7) {
      const top = arr.slice(0, 6);
      const rest = arr.slice(6);
      top.push({
        name: `Others (${rest.length})`,
        lines: rest.reduce((s, e) => s + e.lines, 0),
        value: rest.reduce((s, e) => s + e.value, 0),
        color: OTHERS_COLOR,
      });
      arr = top;
    }
    // Scrap sits AFTER the Others rollup so it's always its own slice, never
    // absorbed. lines: null marks it as amount-only (no count anywhere).
    if (hasScrap) {
      arr = [...arr, { name: "Scrap sales", lines: null, value: scrapThisMonth, color: SCRAP_COLOR, isScrap: true }];
    }
    return {
      slices: arr,
      totalLines: shipped.length,
      totalValue: shipped.reduce((s, i) => s + (Number(i.value_usd) || 0), 0) + (hasScrap ? scrapThisMonth : 0),
    };
  }, [shipped, mode, colorByClient, groupByClientName, canSeeValue, hasScrap, scrapThisMonth]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Shipped in ${monthLabel} by ${mode === "groups" ? "client group" : "client"}`}
      data-testid="client-donut-lightbox"
    >
      <div
        className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-2xl shadow-2xl w-[560px] max-w-[94vw] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: title · Clients/Groups capsule · close */}
        <div className="flex items-center gap-2.5">
          <PieChartIcon className="w-4 h-4 text-emerald-500 shrink-0" />
          <h3 className="text-sm font-semibold">Shipped{hasScrap ? " + scrap" : ""} in {monthLabel}</h3>
          <div className="ml-auto inline-flex items-center rounded-full rl-carved bg-[hsl(var(--muted))]/60 p-[3px] text-[11px]" role="group" aria-label="Slice by">
            {[
              { key: "clients", label: "Clients" },
              { key: "groups", label: "Groups" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                aria-pressed={mode === key}
                className={`px-3 py-[3px] rounded-full font-medium transition-colors ${
                  mode === key
                    ? "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                }`}
                data-testid={`donut-mode-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full rl-well text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            aria-label="Close"
            data-testid="donut-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {totalLines === 0 && !hasScrap ? (
          <div className="py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
            Nothing shipped yet this month.
          </div>
        ) : (
          <div className="flex items-center gap-5 mt-4 flex-wrap sm:flex-nowrap">
            {/* Donut + centre totals */}
            <div className="relative w-[180px] h-[180px] shrink-0 mx-auto sm:mx-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey={canSeeValue ? "value" : "lines"}
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={86}
                    paddingAngle={slices.length > 1 ? 2 : 0}
                    stroke="hsl(var(--card))"
                    strokeWidth={2}
                    isAnimationActive={true}
                  >
                    {slices.map((s) => (
                      <Cell key={s.name} fill={s.color} />
                    ))}
                  </Pie>
                  <ReTooltip content={<DonutTooltip canSeeValue={canSeeValue} />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                {hasScrap ? (
                  <>
                    <span className="font-mono-data text-[20px] font-bold leading-none text-emerald-600 dark:text-emerald-400">{fmtMoney(totalValue)}</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">total value</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{totalLines} shipped line{totalLines === 1 ? "" : "s"} · incl. scrap</span>
                  </>
                ) : (
                  <>
                    <span className="font-mono-data text-[22px] font-bold leading-none">{totalLines}</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">line{totalLines === 1 ? "" : "s"}</span>
                    {canSeeValue && (
                      <span className="font-mono-data text-[13px] font-semibold text-emerald-600 dark:text-emerald-400 mt-0.5">
                        {fmtMoney(totalValue)}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Legend */}
            <div className="flex-1 min-w-[220px] flex flex-col gap-[7px] text-[12px]">
              {slices.map((s) => (
                <div key={s.name} className="flex items-center gap-2" data-testid={`donut-row-${s.name}`}>
                  <span className="w-[9px] h-[9px] rounded-full shrink-0" style={{ background: s.color }} />
                  <span className={`font-semibold truncate ${s.isScrap ? "text-violet-700 dark:text-violet-300" : ""}`}>{s.name}</span>
                  <span className="ml-auto font-mono-data text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                    {s.isScrap
                      ? <span className="text-[9px] uppercase tracking-wider font-semibold text-violet-500/80 not-italic">by-product</span>
                      : <>{s.lines} line{s.lines === 1 ? "" : "s"}</>}
                  </span>
                  {canSeeValue && (
                    <span className="font-mono-data font-semibold w-[64px] text-right whitespace-nowrap">{fmtMoney(s.value)}</span>
                  )}
                </div>
              ))}
              <div className="border-t border-[hsl(var(--border))]/60 mt-1 pt-1.5 text-[10.5px] text-[hsl(var(--muted-foreground))]">
                Slices sized by {canSeeValue ? "value" : "line count"}{hasScrap ? " · scrap shown as amount only" : ""} · hover a slice for detail
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
